import type { JSX } from 'react';
import { Box, Modal, ModalBody, ModalCloseButton, useToast } from '@wistia/vhs';
import { Icon, Heading, IconButton } from '@wistia/ui';
import { downloadFile } from '~/utilities/downloadFile';
import { isNonEmptyString, isNotNil } from '~/utilities/type-guards';
import { useDownloadExtendedAudioDescriptionFileMutation } from './__gql/ExtendedAudioDescriptionMp3Files.gql.gen';

export const DOWNLOAD_OPTIONS = [
  { name: 'mp3', format: 'mp3' },
  { name: 'WebVTT', format: 'webvtt' },
  { name: 'WebVTT (extended)', format: 'webvtt_extended' },
];

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  threeplayAudioDescriptionId: string;
};

export const DownloadExtendedAudioDescriptionFilesModal = ({
  isOpen,
  closeModal,
  threeplayAudioDescriptionId,
}: Props): JSX.Element => {
  const showToast = useToast();
  const [downloadExtendedAudioDescriptionFileMutation] =
    useDownloadExtendedAudioDescriptionFileMutation();

  const handleDownload = (format: string) => {
    void downloadExtendedAudioDescriptionFileMutation({
      variables: {
        input: {
          audioDescriptionId: threeplayAudioDescriptionId,
          format,
        },
      },
      onCompleted: (response) => {
        if (isNotNil(response.downloadExtendedAudioDescriptionFile)) {
          const { downloadUrl, fileData } = response.downloadExtendedAudioDescriptionFile;

          if (isNonEmptyString(downloadUrl)) {
            window.open(downloadUrl, '_blank');
          } else {
            downloadFile(fileData, `${format}.vtt`, 'text/plain');
          }
        } else {
          showToast({
            variant: 'error',
            message: 'Cannot download file. Please contact customer support.',
          });
        }
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      style={{ padding: '2rem', minWidth: '22rem' }}
    >
      <ModalCloseButton />
      <ModalBody style={{ marginTop: 0 }}>
        <Heading
          variant="heading2"
          style={{ marginBottom: '1.5rem' }}
        >
          Download
        </Heading>
        <Box
          direction="column"
          gap="1rem"
        >
          {DOWNLOAD_OPTIONS.map(({ name, format }) => (
            <Box
              key={name}
              direction="row"
              gap="1rem"
              alignItems="center"
              justifyContent="between"
              style={{ width: '100%' }}
            >
              <span>{name}</span>
              <IconButton
                onClick={() => handleDownload(format)}
                label="download"
                colorScheme="standard"
                variant="ghost"
              >
                <Icon
                  type="download"
                  size="lg"
                />
              </IconButton>
            </Box>
          ))}
        </Box>
      </ModalBody>
    </Modal>
  );
};
